import React, { useState } from 'react';
import {
    Plus,
    Trash2,
    Edit3,
    MessageCircle,
    Save,
    X
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * ChatMenu Component
 * 
 * Redesigned to match the "Identity" tab aesthetic.
 * Manages the "Chat Menu" / "FAQ" options for the chatbot.
 */
function ChatMenu({
    menuItems = [],
    setMenuItems,
    updateConfig,
    saveConfig,
    chatbotId
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [newItem, setNewItem] = useState({
        name: '',
        type: 'FAQ',
        action: ''
    });

    // Reset form
    const resetForm = () => {
        setNewItem({ name: '', type: 'FAQ', action: '' });
        setEditingIndex(-1);
        setIsEditing(false);
    };

    const handleAddItem = () => {
        if (!newItem.name.trim()) return;
        if (newItem.type === 'FAQ' && !newItem.action.trim()) return;

        let updatedItems;
        if (editingIndex >= 0) {
            updatedItems = [...menuItems];
            updatedItems[editingIndex] = { ...newItem };
        } else {
            updatedItems = [...menuItems, { ...newItem, id: Date.now() }];
        }

        // Update state via prop
        setMenuItems(updatedItems);

        // Trigger parent update & save
        updateConfig({ menuItems: updatedItems });
        if (saveConfig) {
            saveConfig({ menuItems: updatedItems });
        }

        resetForm();
    };

    const handleEditItem = (index) => {
        setNewItem({ ...menuItems[index] });
        setEditingIndex(index);
        setIsEditing(true);
    };

    const handleDeleteItem = (index) => {
        if (window.confirm('Delete this menu item?')) {
            const updatedItems = menuItems.filter((_, i) => i !== index);
            setMenuItems(updatedItems);
            updateConfig({ menuItems: updatedItems });
            if (saveConfig) {
                saveConfig({ menuItems: updatedItems });
            }
        }
    };

    // Helper for Rich Text
    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold tracking-tight text-slate-900">Chat Menu</h2>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">Configure FAQs and quick actions for your chatbot.</p>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="max-w-3xl mx-auto px-8 py-8 flex flex-col gap-8">

                        {/* Add / Edit Section */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    {isEditing ? <Edit3 size={16} /> : <Plus size={16} />}
                                    {isEditing ? 'Edit Menu Item' : 'Add New Item'}
                                </h3>
                                {isEditing && (
                                    <button onClick={resetForm} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
                                        <X size={12} /> Cancel
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="col-span-2 space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-600">Label</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Pricing Plans"
                                        value={newItem.name}
                                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-600">Type</label>
                                    <select
                                        value={newItem.type}
                                        onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800"
                                    >
                                        <option value="FAQ">FAQ Response</option>
                                        {/* TODO: Restore the Script option after it is ready for release. */}
                                    </select>
                                </div>
                            </div>

                            {newItem.type === 'FAQ' && (
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-600">Response Message</label>
                                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                        <ReactQuill
                                            theme="snow"
                                            value={newItem.action}
                                            onChange={(val) => setNewItem({ ...newItem, action: val })}
                                            modules={modules}
                                            placeholder="Enter the answer..."
                                            className="text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    onClick={handleAddItem}
                                    disabled={!newItem.name.trim() || (newItem.type === 'FAQ' && !newItem.action.trim())}
                                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                >
                                    {isEditing ? <Save size={14} /> : <Plus size={14} />}
                                    {isEditing ? 'Save Changes' : 'Add Item'}
                                </button>
                            </div>
                        </div>

                        {/* List Section */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-slate-900">Current Menu Items</h3>

                            {menuItems.length === 0 ? (
                                <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl bg-slate-50/30">
                                    <MessageCircle size={24} className="mx-auto text-slate-300 mb-2" />
                                    <p className="text-sm text-slate-500">No menu items yet.</p>
                                </div>
                            ) : (
                                <div className="grid gap-3">
                                    {menuItems.map((item, index) => (
                                        <div key={item.id || index} className="group bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm hover:border-slate-300 transition-all flex items-start justify-between">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-800 text-sm">{item.name}</span>
                                                    <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">{item.type}</span>
                                                </div>
                                                {item.type === 'FAQ' && (
                                                    <div className="text-xs text-slate-500 line-clamp-2" dangerouslySetInnerHTML={{ __html: item.action }} />
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEditItem(index)}
                                                    className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteItem(index)}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChatMenu;
